/**
 * Backend calls for the per-platform minimum app build (identity `/appVersionPolicies`).
 *
 * Role rules are enforced server-side in every environment: any admin may lower the required
 * build or extend the grace period; only `superAdmin` may raise the required build (or create a
 * policy with `requiredBuild > 0`) or move `requiredEffectiveAt` earlier. A raise of more than
 * 1 000 above the previous floor must carry `confirmRequiredBuild === requiredBuild`, else the
 * backend answers 400 `appVersionPolicyConfirmationRequired`. Updates are guarded by `updatedAt`
 * (409 `incorrectVersion` when stale). Lowering to 0 disables the gate; there is no DELETE.
 */
import type { AppPlatform, AppVersionPolicyRecord } from "@/firestore/appVersionPolicies";
import { adminRequest } from "./client";

/** 400 `errorCode` when a large raise lacks the retyped confirmation. */
export const APP_VERSION_POLICY_CONFIRMATION_REQUIRED = "appVersionPolicyConfirmationRequired";
/** 409 `errorCode` when the `updatedAt` guard no longer matches. */
export const INCORRECT_VERSION = "incorrectVersion";

export interface AppVersionPolicyCreateInput {
  platform: AppPlatform;
  requiredBuild: number;
  recommendedBuild: number;
  requiredEffectiveAt?: string | null;
  message?: string | null;
  storeUrl?: string | null;
  reason: string;
  confirmRequiredBuild?: number;
}

/** Subset of the mutable fields + the mandatory `reason` (+ confirmation on a large jump). */
export interface AppVersionPolicyUpdateInput {
  requiredBuild?: number;
  recommendedBuild?: number;
  requiredEffectiveAt?: string | null;
  message?: string | null;
  storeUrl?: string | null;
  reason: string;
  confirmRequiredBuild?: number;
}

export type AppVersionPolicyCommand =
  | { kind: "create"; input: AppVersionPolicyCreateInput }
  | { kind: "update"; id: string; updatedAt: string; input: AppVersionPolicyUpdateInput };

/** `GET /appVersionPolicies` — every live policy, full rows incl. `reason` / `updatedBy`. */
export async function listAppVersionPolicies(
  token: string,
  signal?: AbortSignal,
): Promise<AppVersionPolicyRecord[]> {
  const page = await adminRequest<{ items: AppVersionPolicyRecord[] }>({
    method: "GET",
    path: "/appVersionPolicies",
    token,
    signal,
  });
  return page.items;
}

/** `POST /appVersionPolicies` — create the platform's policy (400 if one already exists). */
export function createAppVersionPolicy(
  token: string,
  body: AppVersionPolicyCreateInput,
): Promise<AppVersionPolicyRecord> {
  return adminRequest<AppVersionPolicyRecord>({
    method: "POST",
    path: "/appVersionPolicies",
    body,
    token,
  });
}

/** `PATCH /appVersionPolicies/{id}?updatedAt=` — optimistic-concurrency update. */
export function updateAppVersionPolicy(
  token: string,
  id: string,
  updatedAt: string,
  body: AppVersionPolicyUpdateInput,
): Promise<AppVersionPolicyRecord> {
  return adminRequest<AppVersionPolicyRecord>({
    method: "PATCH",
    path: `/appVersionPolicies/${encodeURIComponent(id)}`,
    query: { updatedAt },
    body,
    token,
  });
}
